import { useState, useEffect } from 'react'
import {
    GripVertical, Plus, Trash2, Settings, Eye, Save, ChevronDown, ChevronUp,
    Monitor, Smartphone, X, Check
} from 'lucide-react'
import { useLanguageStore } from '../../lib/store'
import { SECTION_LIBRARY, type SectionConfig, type SectionType } from '../../lib/sections'

interface PageLayout {
    page_slug: string
    page_name: string
    layout_config: { sections: SectionConfig[] }
    sidebar_config: { sections: SectionConfig[] }
}

export default function PageBuilder() {
    const { language } = useLanguageStore()

    const [layout, setLayout] = useState<PageLayout | null>(null)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [showLibrary, setShowLibrary] = useState(false)
    const [addingTo, setAddingTo] = useState<'main' | 'sidebar' | null>(null)
    const [editingSection, setEditingSection] = useState<SectionConfig | null>(null)
    const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop')
    const [categories, setCategories] = useState<any[]>([])

    // Fetch layout and categories
    useEffect(() => {
        Promise.all([
            fetch('/api/admin/page-layouts/home').then(r => r.json()),
            fetch('/api/categories').then(r => r.json())
        ]).then(([layoutRes, catRes]) => {
            if (layoutRes.success && layoutRes.layout) {
                setLayout(layoutRes.layout)
            } else {
                // Create default if not exists
                setLayout({
                    page_slug: 'home',
                    page_name: 'Homepage',
                    layout_config: { sections: [] },
                    sidebar_config: { sections: [] }
                })
            }
            setCategories(catRes.categories || [])
        }).finally(() => setLoading(false))
    }, [])

    // Save layout
    const saveLayout = async () => {
        if (!layout) return
        setSaving(true)
        try {
            const res = await fetch(`/api/admin/page-layouts/${layout.page_slug}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    layout_config: layout.layout_config,
                    sidebar_config: layout.sidebar_config
                })
            })
            const data = await res.json()
            if (data.success) {
                alert(language === 'en' ? 'Layout saved!' : 'Đã lưu!')
            }
        } catch (error) {
            alert(language === 'en' ? 'Failed to save' : 'Lưu thất bại')
        } finally {
            setSaving(false)
        }
    }

    // Add section
    const addSection = (type: SectionType, variant?: string) => {
        if (!layout || !addingTo) return

        const lib = SECTION_LIBRARY[type]
        const newSection: SectionConfig = {
            id: `${type}-${Date.now()}`,
            type,
            variant: variant || lib.variants[0]?.id,
            config: { ...lib.defaultConfig }
        }

        if (addingTo === 'main') {
            setLayout({
                ...layout,
                layout_config: {
                    sections: [...layout.layout_config.sections, newSection]
                }
            })
        } else {
            setLayout({
                ...layout,
                sidebar_config: {
                    sections: [...layout.sidebar_config.sections, newSection]
                }
            })
        }
        setShowLibrary(false)
        setAddingTo(null)
    }

    // Remove section
    const removeSection = (id: string, from: 'main' | 'sidebar') => {
        if (!layout) return
        if (from === 'main') {
            setLayout({
                ...layout,
                layout_config: {
                    sections: layout.layout_config.sections.filter(s => s.id !== id)
                }
            })
        } else {
            setLayout({
                ...layout,
                sidebar_config: {
                    sections: layout.sidebar_config.sections.filter(s => s.id !== id)
                }
            })
        }
    }

    // Move section
    const moveSection = (id: string, direction: 'up' | 'down', from: 'main' | 'sidebar') => {
        if (!layout) return
        const sections = from === 'main' ? [...layout.layout_config.sections] : [...layout.sidebar_config.sections]
        const index = sections.findIndex(s => s.id === id)
        if (index === -1) return

        const newIndex = direction === 'up' ? index - 1 : index + 1
        if (newIndex < 0 || newIndex >= sections.length) return

        [sections[index], sections[newIndex]] = [sections[newIndex], sections[index]]

        if (from === 'main') {
            setLayout({ ...layout, layout_config: { sections } })
        } else {
            setLayout({ ...layout, sidebar_config: { sections } })
        }
    }

    // Update section config
    const updateSectionConfig = (id: string, from: 'main' | 'sidebar', updates: Partial<SectionConfig>) => {
        if (!layout) return
        const sections = from === 'main' ? [...layout.layout_config.sections] : [...layout.sidebar_config.sections]
        const index = sections.findIndex(s => s.id === id)
        if (index === -1) return

        sections[index] = { ...sections[index], ...updates }

        if (from === 'main') {
            setLayout({ ...layout, layout_config: { sections } })
        } else {
            setLayout({ ...layout, sidebar_config: { sections } })
        }
        setEditingSection(null)
    }

    const getName = (lib: any) => lib.name[language]

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full" />
            </div>
        )
    }

    return (
        <div className="h-[calc(100vh-120px)] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-4 pb-4 border-b">
                <div>
                    <h1 className="text-2xl font-bold">{language === 'en' ? 'Page Builder' : 'Trình tạo trang'}</h1>
                    <p className="text-sm text-gray-500">{language === 'en' ? 'Drag & drop sections to build your homepage' : 'Kéo thả các section để xây dựng trang chủ'}</p>
                </div>
                <div className="flex items-center gap-3">
                    {/* Preview mode toggle */}
                    <div className="flex items-center bg-gray-100 rounded-lg p-1">
                        <button
                            onClick={() => setPreviewMode('desktop')}
                            className={`p-2 rounded ${previewMode === 'desktop' ? 'bg-white shadow' : ''}`}
                        >
                            <Monitor className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setPreviewMode('mobile')}
                            className={`p-2 rounded ${previewMode === 'mobile' ? 'bg-white shadow' : ''}`}
                        >
                            <Smartphone className="w-4 h-4" />
                        </button>
                    </div>
                    <a
                        href="/"
                        target="_blank"
                        className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50"
                    >
                        <Eye className="w-4 h-4" />
                        {language === 'en' ? 'Preview' : 'Xem trước'}
                    </a>
                    <button
                        onClick={saveLayout}
                        disabled={saving}
                        className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                    >
                        <Save className="w-4 h-4" />
                        {saving ? (language === 'en' ? 'Saving...' : 'Đang lưu...') : (language === 'en' ? 'Save' : 'Lưu')}
                    </button>
                </div>
            </div>

            {/* Main content */}
            <div className="flex-1 grid grid-cols-3 gap-6 overflow-hidden">
                {/* Main sections */}
                <div className="col-span-2 flex flex-col bg-gray-50 rounded-xl p-4 overflow-auto">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-bold">{language === 'en' ? 'Main Content' : 'Nội dung chính'}</h2>
                        <button
                            onClick={() => { setShowLibrary(true); setAddingTo('main') }}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                        >
                            <Plus className="w-4 h-4" /> {language === 'en' ? 'Add Section' : 'Thêm section'}
                        </button>
                    </div>

                    <div className="space-y-3 flex-1">
                        {layout?.layout_config.sections.map((section, idx) => (
                            <SectionCard
                                key={section.id}
                                section={section}
                                index={idx}
                                total={layout.layout_config.sections.length}
                                categories={categories}
                                language={language}
                                onMove={(dir) => moveSection(section.id, dir, 'main')}
                                onRemove={() => removeSection(section.id, 'main')}
                                onEdit={() => setEditingSection(section)}
                            />
                        ))}
                        {layout?.layout_config.sections.length === 0 && (
                            <div className="flex-1 flex items-center justify-center text-gray-400 border-2 border-dashed rounded-xl p-8">
                                {language === 'en' ? 'Click "Add Section" to start' : 'Nhấn "Thêm section" để bắt đầu'}
                            </div>
                        )}
                    </div>
                </div>

                {/* Sidebar sections */}
                <div className="flex flex-col bg-gray-50 rounded-xl p-4 overflow-auto">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-bold">Sidebar</h2>
                        <button
                            onClick={() => { setShowLibrary(true); setAddingTo('sidebar') }}
                            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                        >
                            <Plus className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="space-y-2 flex-1">
                        {layout?.sidebar_config.sections.map((section, idx) => (
                            <SectionCard
                                key={section.id}
                                section={section}
                                index={idx}
                                total={layout.sidebar_config.sections.length}
                                categories={categories}
                                language={language}
                                compact
                                onMove={(dir) => moveSection(section.id, dir, 'sidebar')}
                                onRemove={() => removeSection(section.id, 'sidebar')}
                                onEdit={() => setEditingSection(section)}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {/* Section Library Modal */}
            {showLibrary && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-auto">
                        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
                            <h3 className="font-bold text-lg">
                                {language === 'en' ? 'Add Section' : 'Thêm Section'}
                                <span className="text-sm font-normal text-gray-500 ml-2">
                                    ({addingTo === 'main' ? (language === 'en' ? 'Main Content' : 'Nội dung') : 'Sidebar'})
                                </span>
                            </h3>
                            <button onClick={() => { setShowLibrary(false); setAddingTo(null) }} className="p-2 hover:bg-gray-100 rounded-lg">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-4 grid grid-cols-2 gap-3">
                            {Object.entries(SECTION_LIBRARY)
                                .filter(([key]) => {
                                    if (addingTo === 'main') {
                                        return ['hero', 'category', 'tours', 'vehicles', 'videos'].includes(key)
                                    }
                                    return ['search', 'categories', 'popular', 'cta', 'newsletter', 'ads'].includes(key)
                                })
                                .map(([key, lib]) => (
                                    <button
                                        key={key}
                                        onClick={() => addSection(key as SectionType)}
                                        className="flex items-center gap-3 p-4 border rounded-xl hover:border-primary-300 hover:bg-primary-50 transition text-left"
                                    >
                                        <span className="text-2xl">{lib.icon}</span>
                                        <div>
                                            <div className="font-medium">{getName(lib)}</div>
                                            {lib.variants.length > 0 && (
                                                <div className="text-xs text-gray-500">
                                                    {lib.variants.length} {language === 'en' ? 'variants' : 'kiểu'}
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Section Config Modal */}
            {editingSection && (
                <SectionConfigModal
                    section={editingSection}
                    categories={categories}
                    language={language}
                    onSave={(updates) => {
                        const from = layout?.layout_config.sections.find(s => s.id === editingSection.id) ? 'main' : 'sidebar'
                        updateSectionConfig(editingSection.id, from, updates)
                    }}
                    onClose={() => setEditingSection(null)}
                />
            )}
        </div>
    )
}

// Section Card Component
function SectionCard({
    section, index, total, categories, language, compact,
    onMove, onRemove, onEdit
}: {
    section: SectionConfig
    index: number
    total: number
    categories: any[]
    language: string
    compact?: boolean
    onMove: (dir: 'up' | 'down') => void
    onRemove: () => void
    onEdit: () => void
}) {
    const lib = SECTION_LIBRARY[section.type]
    const getName = (l: any) => l?.name[language] || section.type

    // Get category name for category sections
    const getCategoryName = () => {
        if (section.type === 'category' && section.config?.categorySlug) {
            const cat = categories.find(c => c.slug === section.config.categorySlug)
            return cat ? (language === 'en' ? cat.name_en : cat.name_vi) : section.config.categorySlug
        }
        return null
    }

    return (
        <div className={`bg-white rounded-lg border p-3 flex items-center gap-3 group hover:shadow-md transition ${compact ? 'p-2' : ''}`}>
            <GripVertical className="w-4 h-4 text-gray-300 cursor-grab" />
            <span className={compact ? 'text-lg' : 'text-2xl'}>{lib?.icon}</span>
            <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{getName(lib)}</div>
                {getCategoryName() && (
                    <div className="text-xs text-gray-500 truncate">{getCategoryName()}</div>
                )}
            </div>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                <button onClick={() => onMove('up')} disabled={index === 0} className="p-1 hover:bg-gray-100 rounded disabled:opacity-30">
                    <ChevronUp className="w-4 h-4" />
                </button>
                <button onClick={() => onMove('down')} disabled={index === total - 1} className="p-1 hover:bg-gray-100 rounded disabled:opacity-30">
                    <ChevronDown className="w-4 h-4" />
                </button>
                <button onClick={onEdit} className="p-1 hover:bg-gray-100 rounded">
                    <Settings className="w-4 h-4" />
                </button>
                <button onClick={onRemove} className="p-1 hover:bg-red-100 text-red-600 rounded">
                    <Trash2 className="w-4 h-4" />
                </button>
            </div>
        </div>
    )
}

// Section Config Modal
function SectionConfigModal({
    section, categories, language, onSave, onClose
}: {
    section: SectionConfig
    categories: any[]
    language: string
    onSave: (updates: Partial<SectionConfig>) => void
    onClose: () => void
}) {
    const [config, setConfig] = useState(section.config || {})
    const [variant, setVariant] = useState(section.variant || '')
    const [sticky, setSticky] = useState(section.sticky || false)

    const lib = SECTION_LIBRARY[section.type]

    const handleSave = () => {
        onSave({ config, variant, sticky })
    }

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-2xl w-full max-w-lg">
                <div className="flex items-center justify-between p-4 border-b">
                    <h3 className="font-bold text-lg flex items-center gap-2">
                        <span>{lib?.icon}</span>
                        {language === 'en' ? 'Section Settings' : 'Cài đặt Section'}
                    </h3>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-4 space-y-4">
                    {/* Variant selector */}
                    {lib?.variants.length > 0 && (
                        <div>
                            <label className="block text-sm font-medium mb-2">{language === 'en' ? 'Layout Style' : 'Kiểu hiển thị'}</label>
                            <div className="grid grid-cols-2 gap-2">
                                {lib.variants.map(v => (
                                    <button
                                        key={v.id}
                                        onClick={() => setVariant(v.id)}
                                        className={`p-3 border rounded-lg text-left transition ${variant === v.id ? 'border-primary-500 bg-primary-50' : 'hover:border-gray-300'}`}
                                    >
                                        <div className="font-medium text-sm">{v.name[language as 'en' | 'vi']}</div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Category selector */}
                    {section.type === 'category' && (
                        <div>
                            <label className="block text-sm font-medium mb-2">{language === 'en' ? 'Category' : 'Danh mục'}</label>
                            <select
                                value={config.categorySlug || ''}
                                onChange={(e) => setConfig({ ...config, categorySlug: e.target.value })}
                                className="w-full px-3 py-2 border rounded-lg"
                            >
                                <option value="">{language === 'en' ? 'Select category' : 'Chọn danh mục'}</option>
                                {categories.map(cat => (
                                    <option key={cat.id} value={cat.slug}>
                                        {cat.icon} {language === 'en' ? cat.name_en : cat.name_vi}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Count */}
                    {(section.type === 'category' || section.type === 'tours' || section.type === 'vehicles' || section.type === 'videos' || section.type === 'popular') && (
                        <div>
                            <label className="block text-sm font-medium mb-2">{language === 'en' ? 'Number of items' : 'Số lượng'}</label>
                            <input
                                type="number"
                                min={1}
                                max={20}
                                value={config.count || 3}
                                onChange={(e) => setConfig({ ...config, count: parseInt(e.target.value) })}
                                className="w-full px-3 py-2 border rounded-lg"
                            />
                        </div>
                    )}

                    {/* Columns */}
                    {(section.type === 'category' || section.type === 'vehicles') && (
                        <div>
                            <label className="block text-sm font-medium mb-2">{language === 'en' ? 'Columns' : 'Số cột'}</label>
                            <div className="flex gap-2">
                                {[2, 3, 4].map(n => (
                                    <button
                                        key={n}
                                        onClick={() => setConfig({ ...config, columns: n })}
                                        className={`flex-1 p-2 border rounded-lg ${config.columns === n ? 'border-primary-500 bg-primary-50' : ''}`}
                                    >
                                        {n}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Show image toggle */}
                    {section.type === 'category' && (
                        <div className="flex items-center justify-between">
                            <label className="text-sm font-medium">{language === 'en' ? 'Show images' : 'Hiện ảnh'}</label>
                            <button
                                onClick={() => setConfig({ ...config, showImage: !config.showImage })}
                                className={`w-12 h-6 rounded-full transition ${config.showImage !== false ? 'bg-primary-600' : 'bg-gray-300'}`}
                            >
                                <span className={`block w-5 h-5 bg-white rounded-full shadow transition ${config.showImage !== false ? 'translate-x-6' : 'translate-x-0.5'}`} />
                            </button>
                        </div>
                    )}

                    {/* CTA settings */}
                    {section.type === 'cta' && (
                        <>
                            <div>
                                <label className="block text-sm font-medium mb-2">{language === 'en' ? 'Title' : 'Tiêu đề'}</label>
                                <input
                                    type="text"
                                    value={config.title || ''}
                                    onChange={(e) => setConfig({ ...config, title: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2">Link</label>
                                <input
                                    type="text"
                                    value={config.link || ''}
                                    onChange={(e) => setConfig({ ...config, link: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                />
                            </div>
                        </>
                    )}

                    {/* Ads settings */}
                    {section.type === 'ads' && (
                        <>
                            <div>
                                <label className="block text-sm font-medium mb-2">{language === 'en' ? 'Image URL' : 'URL ảnh'}</label>
                                <input
                                    type="text"
                                    value={config.imageUrl || ''}
                                    onChange={(e) => setConfig({ ...config, imageUrl: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg"
                                    placeholder="https://..."
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2">{language === 'en' ? 'Custom HTML/JS' : 'HTML/JS tùy chỉnh'}</label>
                                <textarea
                                    value={config.html || ''}
                                    onChange={(e) => setConfig({ ...config, html: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg h-24 font-mono text-sm"
                                    placeholder="<script>...</script>"
                                />
                            </div>
                        </>
                    )}

                    {/* Sticky toggle for sidebar */}
                    {['newsletter', 'cta', 'ads'].includes(section.type) && (
                        <div className="flex items-center justify-between">
                            <label className="text-sm font-medium">{language === 'en' ? 'Sticky (fixed position)' : 'Cố định khi cuộn'}</label>
                            <button
                                onClick={() => setSticky(!sticky)}
                                className={`w-12 h-6 rounded-full transition ${sticky ? 'bg-primary-600' : 'bg-gray-300'}`}
                            >
                                <span className={`block w-5 h-5 bg-white rounded-full shadow transition ${sticky ? 'translate-x-6' : 'translate-x-0.5'}`} />
                            </button>
                        </div>
                    )}
                </div>

                <div className="flex justify-end gap-3 p-4 border-t">
                    <button onClick={onClose} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                        {language === 'en' ? 'Cancel' : 'Hủy'}
                    </button>
                    <button onClick={handleSave} className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                        <Check className="w-4 h-4" />
                        {language === 'en' ? 'Save' : 'Lưu'}
                    </button>
                </div>
            </div>
        </div>
    )
}
